/**
 * The component wrapper around the Markdown renderer.
 *
 * It exists so that message bodies have one import site and one place where
 * memoisation happens: a room re-renders on every arriving message, and
 * re-parsing every visible message each time would make a busy channel feel
 * heavy for no reason.
 */

import { memo, useMemo } from 'react'
import type { ReactElement } from 'react'

import { renderInlineMarkdown, renderMarkdown } from '@/lib/markdown'
import styles from './Markdown.module.css'

function cx(...values: Array<string | false | null | undefined>): string {
  return values
    .filter((value): value is string => typeof value === 'string' && value !== '')
    .join(' ')
}

export interface MarkdownProps {
  /** Plain text, exactly as `Message.text` carries it. Never HTML. */
  text: string
  /** The reader, so a mention of them can be drawn more strongly. */
  currentUsername?: string | null
  /** From `Message.mentions_everyone`. */
  mentionsEveryone?: boolean
  /**
   * Collapses the document to a single line with no block structure, for
   * previews and one-line summaries.
   */
  inline?: boolean
  className?: string
}

function MarkdownBody({
  text,
  currentUsername = null,
  mentionsEveryone = false,
  inline = false,
  className,
}: MarkdownProps): ReactElement {
  const content = useMemo(() => {
    const options = { currentUsername, mentionsEveryone }
    return inline ? renderInlineMarkdown(text, options) : renderMarkdown(text, options)
  }, [text, currentUsername, mentionsEveryone, inline])

  if (inline) {
    return <span className={cx(styles.markdown, className)}>{content}</span>
  }

  return <div className={cx(styles.markdown, className)}>{content}</div>
}

export const Markdown = memo(MarkdownBody)
